import { message, UploadFile } from "antd";
import { useTranslate } from ".";
import { useState } from "react";
import { isEqual } from "lodash";
import { BaseRecord, CreateResponse, useCustomMutation } from "@refinedev/core";
import { CreateUploadableAssetRequest } from "pages/media/types/";
import {
  RecursivePartial,
  StagedAsset,
  UploadFileCustom,
  UploadFilesCustomUrl,
} from "components/uploader/types/";
import { AGGREGATED_DOCUMENT_NAME } from "@libertify/ui";
import { toUploadPathSegment } from "./helpers";

export { AGGREGATED_DOCUMENT_NAME };
// `hidden` (MED-6970) lives on the `UploadDefinition` schema the document
// upload endpoint actually accepts, which isn't part of the
// `CreateUploadableAssetRequest` union below (that union covers this hook's
// other callers — brand kit/voice/media assets, none of which have `hidden`).
type DocumentUploadProperties = {
  aggregate_documents?: boolean;
  hidden?: boolean;
};
type AssetRequestProperties =
  | (RecursivePartial<CreateUploadableAssetRequest> & DocumentUploadProperties)
  | ((
      file: UploadFileCustom | undefined
    ) => RecursivePartial<CreateUploadableAssetRequest> &
      DocumentUploadProperties);

export const useUploadFiles = ({
  assetRequestProperties,
  stagedAssets,
}: {
  assetRequestProperties: AssetRequestProperties;
  stagedAssets: StagedAsset | undefined;
}) => {
  const t = useTranslate();
  const method = "put"; // S3 is configured to only accept PUT

  const [fileList, setFileList] = useState<UploadFileCustom[]>([]);

  const { mutateAsync } = useCustomMutation<BaseRecord>();

  const customRequest = ({
    file,
    url,
    onProgress,
    onSuccess,
    onError,
  }: {
    file: UploadFile;
    url: string;
    onProgress?: (event: { percent: number }) => void;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    // NB: S3 requires this header to be empty, but that is subject to change since that is against the HTTP spec.
    xhr.setRequestHeader("Content-Type", "");

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (event.lengthComputable) {
        const percent = (event.loaded / event.total) * 100;
        onProgress?.({ percent });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onSuccess?.();
      } else {
        onError?.(new Error(t("src.components.UploadFiles.uploadFailed")));
      }
    };

    xhr.onerror = () => {
      onError?.(new Error(t("src.components.UploadFiles.uploadFailed")));
    };

    xhr.send(file.originFileObj as Blob);

    return () => xhr.abort();
  };

  const computeProps = (file: UploadFileCustom | undefined) =>
    typeof assetRequestProperties === "function"
      ? assetRequestProperties(file)
      : assetRequestProperties;

  const assetRequestProps = computeProps(fileList[0]);
  const hasAggregateDocuments = "aggregate_documents" in assetRequestProps;

  /**
   * Files that actually opt into the merge — each file's OWN props say so
   * (`aggregate_documents: true`), not merely "the scheme is in play for
   * this hook usage" (`hasAggregateDocuments`). A caller with a per-file
   * function (e.g. Documents' hidden/Extras split) can flag some files out
   * of the merge entirely by returning `false` for them.
   */
  const aggregatableFiles = hasAggregateDocuments
    ? fileList.filter((f) => Boolean(computeProps(f).aggregate_documents))
    : [];

  //the first document is a merge of the documents that will be put afterwards from the backend
  const aggregatedDocumentsBase:
    | (UploadFile & { abort: () => void })
    | undefined =
    aggregatableFiles.length > 1
      ? {
          name: AGGREGATED_DOCUMENT_NAME,
          uid: "default",
          percent: 100,
          abort: () => undefined,
        }
      : undefined;

  const generateUploadValues = (
    file: UploadFileCustom,
    filesInThisUpload: UploadFileCustom[]
  ) => {
    const isAggregatedFileBase =
      hasAggregateDocuments && isEqual(file, aggregatedDocumentsBase);

    // Evaluate the request properties per file so each upload keeps its own
    // values (e.g. `description` derived from the file name). The synthetic
    // aggregate base file has no real counterpart, so it reuses the first
    // file's props to preserve the previous behavior.
    const sourceFile = isAggregatedFileBase ? fileList[0] : file;
    const perFileProps = computeProps(sourceFile);

    let assetProps = perFileProps;
    if (hasAggregateDocuments) {
      const { aggregate_documents, ...rest } = perFileProps;
      assetProps = rest;
    }

    let aggregatedProps = {};
    if (isAggregatedFileBase) {
      // The merge target represents the combined Source content, not any one
      // file's own visibility — always visible regardless of file 0's flag.
      aggregatedProps = { index: 0, aggregate_documents: true, hidden: false };
    } else if (hasAggregateDocuments && Boolean(perFileProps.aggregate_documents)) {
      // Position among the OTHER files in THIS upload that also opt into the
      // merge — not the raw position in `filesInThisUpload`. A file that
      // opted out (e.g. hidden) sitting between two merged files must not
      // shift their index and break the backend's assembly order.
      const participating = filesInThisUpload.filter(
        (f) =>
          !isEqual(f, aggregatedDocumentsBase) &&
          Boolean(computeProps(f).aggregate_documents)
      );
      const position = participating.findIndex((f) => f.uid === file.uid);
      aggregatedProps = { index: position + 1, aggregate_documents: false };
    }

    const uploadValues = {
      ...assetProps,
      ...aggregatedProps,
      name: file.name,
      asset: {},
      ...stagedAssets?.find((asset) => asset.uid === file.uid),
    };

    if ("asset" in perFileProps && perFileProps.asset) {
      uploadValues.asset = {
        ...perFileProps.asset,
        file_name: file.name,
      };
    }

    return uploadValues;
  };

  const getS3LinksToUploadFiles = async ({
    fileListToUpload,
    customUrl,
    action,
    showErrorNotification,
  }: {
    fileListToUpload: UploadFileCustom[];
    customUrl?: UploadFilesCustomUrl;
    action?: string;
    showErrorNotification?: boolean;
  }) => {
    let url: string | undefined;

    const filesInThisUpload = fileListToUpload.filter(Boolean);
    const apiPromises = filesInThisUpload.map((file) => {
      const values = generateUploadValues(file, filesInThisUpload);

      // A function customUrl receives the file name as a safe path segment
      // (see toUploadPathSegment), and its result is sent as-is. A string
      // URL keeps the previous whole-URL encodeURI behaviour.
      const isFunctionUrl = typeof customUrl === "function";
      url = isFunctionUrl
        ? customUrl(toUploadPathSegment(file.name))
        : (customUrl ?? action);

      if (!url) {
        throw new Error("No upload URL provided for file: " + file.name);
      }

      return mutateAsync({
        url: isFunctionUrl ? url : encodeURI(url),
        method: "post",
        values,
      });
    });
    let responses: CreateResponse<BaseRecord>[] = [];
    try {
      responses = await Promise.all(apiPromises);

      return responses;
    } catch (error) {
      if (showErrorNotification !== false) {
        message.error(t("src.components.UploadFiles.uploadFailed"));
      }
      throw error;
    }
  };

  const onFileUploadProgress = (
    file: UploadFileCustom,
    event: { percent: number }
  ) => {
    const updatedFile = { ...file, percent: event.percent };

    setFileList((prevList) =>
      prevList.map((item) => (item.uid === file.uid ? updatedFile : item))
    );
  };
  const onFileUploadSuccess = (file: UploadFileCustom) => {
    const updatedFile = {
      ...file,
      percent: 100,
      status: "done" as const,
    };
    setFileList((prevList) =>
      prevList.map((item) => (item.uid === file.uid ? updatedFile : item))
    );
    return updatedFile;
  };

  const onFileUploadError = (file: UploadFileCustom) => {
    const updatedFile = { ...file, status: "error" as const };
    setFileList((prevList) =>
      prevList.map((item) => (item.uid === file.uid ? updatedFile : item))
    );
    return updatedFile;
  };

  const createUploadPromise = (
    file: UploadFileCustom,
    s3Link: CreateResponse<BaseRecord>,
    showErrorNotification: boolean
  ) => {
    return new Promise((resolve, reject) => {
      file.abort = customRequest({
        file,
        url: s3Link.data.target || s3Link.data.target_signed_dict,
        onProgress: (event) => onFileUploadProgress(file, event),
        onSuccess: () => {
          const updatedFile = onFileUploadSuccess(file);
          resolve(updatedFile);
        },
        onError: (error: Error) => {
          onFileUploadError(file);
          if (showErrorNotification) {
            message.error({
              key: "upload-failed",
              content: t("src.components.UploadFiles.uploadFailed"),
            });
          }
          reject(error);
        },
      });
    });
  };

  const uploadFileToS3 = (
    fileListToUpload: UploadFileCustom[],
    s3Links: CreateResponse<BaseRecord>[],
    { showErrorNotification = true }: { showErrorNotification?: boolean } = {}
  ) => {
    const uploadPromises = fileListToUpload
      .filter(Boolean)
      .map((file, index) => {
        const s3Link = s3Links[index];
        if (!s3Link) {
          throw new Error("No S3 link provided");
        }
        return createUploadPromise(file, s3Link, showErrorNotification);
      });

    return uploadPromises;
  };

  return {
    method,
    hasAggregateDocuments,
    aggregatedDocumentsBase,
    fileList,
    setFileList,
    getS3LinksToUploadFiles,
    uploadFileToS3,
  };
};
